import { createTheme } from '@mui/material'
import { uiPalette } from './uiPalette'
import { DEFAULT_ACCENT_ID, resolveAccentMain } from './accentPresets'
import { deriveMuahangFromMain } from './deriveAccentPalette'

export function createAppTheme(accentId: string = DEFAULT_ACCENT_ID) {
  const main = resolveAccentMain(accentId)
  const d = deriveMuahangFromMain(main)

  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: d.primaryMain,
        dark: d.primaryDark,
        light: d.primaryLight,
        contrastText: '#ffffff',
      },
      muahang: {
        headerBg: d.headerBg,
        navItemActiveBg: d.navItemActiveBg,
        navItemActiveBgHover: d.navItemActiveBgHover,
        kiotSubmenuPanelBg: d.kiotSubmenuPanelBg,
        invoiceTableHeaderBg: d.invoiceTableHeaderBg,
        tableLevel2HeadBg: d.tableLevel2HeadBg,
        tableLevel2HeadBorder: d.tableLevel2HeadBorder,
        tableHeadBg: d.tableHeadBg,
        tableHeadBorder: d.tableHeadBorder,
        rowHoverBg: d.rowHoverBg,
        rowSelectedBg: d.rowSelectedBg,
        rowSelectedHoverBg: d.rowSelectedHoverBg,
        primarySurface: d.primarySurface,
        primarySurfaceBorder: d.primarySurfaceBorder,
        primaryHoverRgba: d.primaryHoverRgba,
      },
      secondary: {
        main: uiPalette.secondary.main,
        dark: uiPalette.secondary.dark,
        light: uiPalette.secondary.light,
        contrastText: '#ffffff',
      },
      error: { main: uiPalette.status.error },
      warning: { main: uiPalette.status.warning },
      info: { main: d.primaryLight },
      success: { main: uiPalette.status.success },
      text: {
        primary: uiPalette.neutral.text,
        secondary: uiPalette.neutral.textMuted,
      },
      background: {
        default: uiPalette.neutral.background,
        paper: '#ffffff',
      },
      divider: uiPalette.neutral.borderMedium,
    },
    typography: {
      fontFamily: 'Arial, Tahoma, "Segoe UI", "Helvetica Neue", sans-serif',
      fontSize: 14,
      h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.25 },
      h2: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.3 },
      h3: { fontSize: '1.75rem', fontWeight: 650, lineHeight: 1.3 },
      h4: { fontSize: '1.375rem', fontWeight: 650, lineHeight: 1.35 },
      h5: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.35 },
      h6: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.35 },
      body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.4 },
      body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.35 },
      subtitle1: { fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.35 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.35 },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        lineHeight: 1.2,
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 4,
    },
    spacing: 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            color: uiPalette.neutral.text,
            backgroundColor: uiPalette.neutral.background,
            fontFamily: 'Arial, Tahoma, "Segoe UI", "Helvetica Neue", sans-serif',
            fontSize: '14px',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: uiPalette.shell.headerShadow,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderColor: uiPalette.neutral.borderMedium,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            color: uiPalette.table.headText,
            fontSize: '0.8125rem',
            backgroundColor: d.tableHeadBg,
            paddingTop: 7,
            paddingBottom: 7,
            paddingLeft: 8,
            paddingRight: 8,
          },
          body: {
            fontSize: '0.8125rem',
            color: uiPalette.neutral.text,
            paddingTop: 7,
            paddingBottom: 7,
            paddingLeft: 8,
            paddingRight: 8,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: d.rowHoverBg,
            },
            '&.Mui-selected': {
              backgroundColor: d.rowSelectedBg,
            },
            '&.Mui-selected:hover': {
              backgroundColor: d.rowSelectedHoverBg,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 3,
            paddingInline: 10,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 3,
            backgroundColor: '#ffffff',
          },
        },
      },
    },
  })
}
